// =============================================================================
// fetchAllRows, chargement complet d'une requête par lots de 1 000 lignes.
//
// Remplace les `.limit()` silencieux : la liste complète est chargée, puis la
// pagination se fait côté écran. Au-delà de 20 000 lignes le chargement
// s'arrête et `truncated` vaut true : l'écran affiche alors un message
// invitant à affiner les filtres.
// =============================================================================

export const FETCH_ALL_BATCH_SIZE = 1000;
export const FETCH_ALL_MAX_ROWS = 20000;

export interface FetchAllResult<Row> {
  rows: Row[];
  /** true quand le plafond de 20 000 lignes a été atteint. */
  truncated: boolean;
}

interface BatchResponse<Row> {
  data: Row[] | null;
  error: { message?: string } | null;
}

/**
 * Exécute `buildQuery(from, to)` (un `.range()` attendu) par lots successifs
 * jusqu'à épuisement ou plafond. `buildQuery` doit reconstruire la requête à
 * chaque appel : les builders PostgREST ne sont pas réutilisables.
 */
export async function fetchAllRows<Row>(
  buildQuery: (from: number, to: number) => PromiseLike<BatchResponse<Row>>,
): Promise<FetchAllResult<Row>> {
  const rows: Row[] = [];
  let truncated = false;
  for (let from = 0; ; from += FETCH_ALL_BATCH_SIZE) {
    const { data, error } = await buildQuery(from, from + FETCH_ALL_BATCH_SIZE - 1);
    if (error) throw error;
    const batch = data ?? [];
    rows.push(...batch);
    if (batch.length < FETCH_ALL_BATCH_SIZE) break;
    if (rows.length >= FETCH_ALL_MAX_ROWS) {
      truncated = true;
      rows.length = FETCH_ALL_MAX_ROWS;
      break;
    }
  }
  return { rows, truncated };
}
